/**
 * D35's notice on an editor with unsaved edits when someone else changed (or
 * deleted) what is being edited: non-blocking — the editor stays usable, and
 * nothing the user typed is replaced — with the two choices a changed
 * resource offers:
 *
 *   Overwrite with mine            save the user's edit over theirs (it names
 *                                  their version, so a third writer still 409s)
 *   Discard mine and load theirs   replace the edit with their version
 *
 * A deleted resource offers neither: the edit stays on screen to copy from.
 * There is no merge (D35 defers the three-way merge dialog to phase 8).
 */
import { CircleAlertIcon, Trash2Icon } from 'lucide-react';
import { Button } from './ui/button.js';

export type LiveUpdateNotice = {
  readonly kind: 'updated' | 'deleted';
  /** Who made the change, as the change channel names them ("Someone" for a machine writer). */
  readonly who: string;
  /** When, already formatted for the user. */
  readonly at: string;
  /** What is being edited: "studio", "show style". */
  readonly what: string;
};

export type LiveUpdateBannerProps = {
  readonly notice: LiveUpdateNotice;
  /** A save is in flight (the overwrite): the choices wait for it. */
  readonly pending: boolean;
  /** Whether the user may save at all (without the permission, only "load theirs" is offered). */
  readonly canSave: boolean;
  readonly onOverwrite: () => void;
  readonly onDiscard: () => void;
};

export function LiveUpdateBanner({ notice, pending, canSave, onOverwrite, onDiscard }: LiveUpdateBannerProps) {
  if (notice.kind === 'deleted') {
    return (
      <div role="status" className="flex flex-wrap items-center gap-3 rounded-md border border-destructive bg-destructive px-4 py-2 text-sm text-destructive-foreground" data-testid="live-update-banner" data-kind="deleted">
        <Trash2Icon className="size-4 shrink-0" aria-hidden="true" />
        <span className="min-w-0 flex-1">
          <strong>{notice.who}</strong> deleted this {notice.what} at {notice.at}. Your edits are still here, but there is nothing left to save them to.
        </span>
      </div>
    );
  }
  return (
    <div role="status" className="flex flex-wrap items-center gap-3 rounded-md border border-warning bg-warning px-4 py-2 text-sm text-warning-foreground" data-testid="live-update-banner" data-kind="updated">
      <CircleAlertIcon className="size-4 shrink-0" aria-hidden="true" />
      <span className="min-w-0 flex-1">
        <strong>{notice.who}</strong> updated this {notice.what} at {notice.at}. Your unsaved edits are kept until you choose.
      </span>
      {canSave ? (
        <Button size="sm" variant="outline" className="border-warning-foreground bg-transparent" disabled={pending} onClick={onOverwrite} data-testid="live-overwrite">
          {pending ? 'Saving…' : 'Overwrite with mine'}
        </Button>
      ) : null}
      <Button size="sm" variant="outline" className="border-warning-foreground bg-transparent" disabled={pending} onClick={onDiscard} data-testid="live-discard">
        Discard mine and load theirs
      </Button>
    </div>
  );
}
